import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import * as styleComplianceService from '../../services/styleCompliance.service';
import { markdownPlainTextToHtml } from '../../utils/formattingUtils';

const SEVERITY_COLOR = { error: 'error', warning: 'warning', suggestion: 'info' };

function stripHtmlForStyleLint(html) {
  return String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** True if stored HTML has real table markup — autofix cannot round-trip tables through plain text. */
function sectionHasTableMarkup(section) {
  const raw = section.output || section.content || '';
  return typeof raw === 'string' && /<\s*table[\s>]/i.test(raw);
}

function findSectionById(sections, sectionId) {
  return (sections || []).find(
    (s) =>
      String(s._id) === String(sectionId) ||
      String(s.sectionId || '') === String(sectionId) ||
      String(s.id || '') === String(sectionId),
  );
}

/**
 * Deterministic writing-style checks (shared YAML catalog on the server) for any section-based
 * document editor. Mirrors the protocol editor's style-check UX; parent supplies section data and
 * an autofix write-back callback so this component stays document-model agnostic.
 */
export default function StyleComplianceCheck({
  sections,
  documentType,
  documentId,
  submissionRegion = 'US',
  onSectionAutofixed,
  getSectionText = (section) => stripHtmlForStyleLint(section.output || section.content || ''),
  buttonLabel = 'Check Style',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [autofixLoading, setAutofixLoading] = useState(false);
  const [autofixError, setAutofixError] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  const buildPayload = () => ({
    documentId: String(documentId || `${documentType}-draft`),
    documentType,
    sections: (sections || [])
      .map((section, idx) => {
        const text = getSectionText(section);
        const sectionId = String(section._id || section.sectionId || section.id || `section-${idx + 1}`);
        return { sectionId, sectionTitle: section.title || '', sectionType: 'all_running_text', text };
      })
      .filter((sec) => sec.text && sec.text.length > 0),
  });

  const runCheck = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setAutofixError(null);
    try {
      const payload = buildPayload();
      if (!payload.sections.length) {
        setError('No section text to analyze. Generate or enter content first.');
        setResult(null);
        return;
      }
      const res = await styleComplianceService.lintDocument(payload, { documentType, submissionRegion });
      setResult(res);
    } catch (e) {
      const rawMsg = e.response?.data?.error || e.response?.data?.message || e.message || 'Style check failed';
      const msg = String(rawMsg || '');
      if (msg.includes('protocol_style_rules')) {
        setError(
          'Style check service is misconfigured: missing the style rules catalog on the server. ' +
            'Please deploy `config/protocol_style_rules.yaml` to the backend.',
        );
      } else {
        setError(msg);
      }
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyAutofix = async () => {
    if (!result?.findings?.length || !onSectionAutofixed) return;
    setAutofixError(null);
    const fixable = result.findings.filter((f) => f.autofixSafe && f.fix && f.sectionId);
    if (!fixable.length) {
      setAutofixError('No autofixable findings in this report. Run a new check after editing.');
      return;
    }
    const bySection = new Map();
    for (const f of fixable) {
      if (!bySection.has(f.sectionId)) bySection.set(f.sectionId, []);
      bySection.get(f.sectionId).push(f);
    }

    setAutofixLoading(true);
    let appliedCount = 0;
    const skippedTitles = [];
    try {
      for (const [sectionId, findingList] of bySection) {
        const section = findSectionById(sections, sectionId);
        if (!section) continue;
        if (section.subsections && section.subsections.length > 0) {
          skippedTitles.push(`${section.title || sectionId} (subsections)`);
          continue;
        }
        if (sectionHasTableMarkup(section)) {
          skippedTitles.push(`${section.title || sectionId} (table)`);
          continue;
        }
        const plain = getSectionText(section);
        if (!plain.trim()) continue;
        const res = await styleComplianceService.applyAutofix(plain, findingList);
        if (res.text && res.text !== plain) {
          const html = markdownPlainTextToHtml(res.text);
          await onSectionAutofixed(section._id || section.sectionId || section.id, html);
          appliedCount += res.appliedFindings?.length ?? findingList.length;
        }
      }
      if (skippedTitles.length) {
        setAutofixError(
          `Skipped (edit manually to avoid losing formatting): ${skippedTitles.join(', ')}.` +
            (appliedCount > 0 ? ' Other sections were updated.' : ''),
        );
      } else if (appliedCount === 0) {
        setAutofixError('No changes were applied.');
      }
      if (appliedCount > 0) {
        await runCheck();
      }
    } catch (e) {
      setAutofixError(e.response?.data?.error || e.message || 'Autofix failed');
    } finally {
      setAutofixLoading(false);
    }
  };

  const summary = result?.summary;
  const findingsBySection = useMemo(() => {
    const map = new Map();
    for (const f of result?.findings || []) {
      const key = f.sectionId || '__document__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return map;
  }, [result]);

  return (
    <>
      <Tooltip title="Run deterministic writing-style checks (terminology, numbers, punctuation) against the regulatory style guide">
        <span>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <SpellcheckIcon />}
            onClick={runCheck}
            disabled={disabled || loading}
          >
            {buttonLabel}
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Style Compliance Check
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={22} />
              <Typography>Running style checks…</Typography>
            </Box>
          )}
          {!loading && error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && result && (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`${summary?.totalFindings ?? 0} findings`} />
                {Object.entries(summary?.bySeverity || {}).map(([sev, count]) =>
                  count > 0 ? (
                    <Chip key={sev} color={SEVERITY_COLOR[sev] || 'default'} label={`${count} ${sev}`} />
                  ) : null,
                )}
                <Chip label={`${summary?.autofixableCount ?? 0} autofixable`} variant="outlined" />
              </Box>
              {autofixError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {autofixError}
                </Alert>
              )}
              {(result.findings || []).length === 0 && <Alert severity="success">No style issues found.</Alert>}
              {[...findingsBySection.entries()].map(([sectionId, findings]) => {
                const section = findSectionById(sections, sectionId);
                const label = section?.title || (sectionId === '__document__' ? 'Document-level' : sectionId);
                const expanded = expandedSection === sectionId;
                return (
                  <Accordion
                    key={sectionId}
                    expanded={expanded}
                    onChange={(_, next) => setExpandedSection(next ? sectionId : null)}
                    sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}
                    elevation={0}
                    disableGutters
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                      <Chip size="small" sx={{ ml: 1 }} label={findings.length} />
                    </AccordionSummary>
                    <AccordionDetails>
                      {findings.map((f) => (
                        <Box key={f.id} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip size="small" color={SEVERITY_COLOR[f.severity] || 'default'} label={f.severity} />
                            <Typography variant="subtitle2">{f.title}</Typography>
                            {f.autofixSafe && <Chip size="small" variant="outlined" label="autofixable" />}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {f.message}
                          </Typography>
                          {f.matchedText && (
                            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                              &quot;{f.matchedText}&quot;
                              {f.suggestedReplacement ? ` → “${f.suggestedReplacement}”` : ''}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {onSectionAutofixed && (
            <Button
              onClick={handleApplyAutofix}
              disabled={autofixLoading || loading || !summary?.autofixableCount}
              startIcon={autofixLoading ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            >
              {autofixLoading ? 'Applying…' : 'Apply Safe Autofixes'}
            </Button>
          )}
          <Button onClick={runCheck} disabled={loading}>
            Re-run Check
          </Button>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
